import { useState } from 'react';
import { BottomSheet } from '../common/BottomSheet';
import { useAuth } from '../../context/AuthContext';
import { resolveClimateZone } from '../../services/api';
import { updateUserCity } from '../../services/auth';

interface CitySetupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CitySetupSheet({ open, onOpenChange }: CitySetupSheetProps) {
  const { user, refreshUserDoc } = useAuth();
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const trimmed = city.trim();
    if (!trimmed || !user) return;

    setLoading(true);
    setError(null);

    try {
      // Resolve climate zone from city name via Cloud Function
      const { climateZone, hemisphere } = await resolveClimateZone(trimmed);

      // Save to user document
      const result = await updateUserCity(user.uid, trimmed, climateZone, hemisphere);
      if (!result.success) {
        setError(result.error);
        return;
      }

      // Refresh context so UI updates
      await refreshUserDoc();
      onOpenChange(false);
      setCity('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setCity('');
      setError(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={handleOpenChange}
      title="Set Your City"
      description="Used to adjust watering schedules for your local climate and seasons."
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="city-input" className="text-xs font-sans text-gray-400 uppercase tracking-wider">
            City
          </label>
          <input
            id="city-input"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g., Portland, OR"
            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-serif text-editorial-black placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            disabled={loading}
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && city.trim()) handleSave();
            }}
          />
        </div>

        {error && (
          <p className="text-sm font-sans text-danger" role="alert">{error}</p>
        )}

        <button
          onClick={handleSave}
          disabled={!city.trim() || loading}
          className="w-full rounded-lg bg-primary py-3 font-sans font-medium text-white transition-opacity disabled:opacity-50"
          aria-label={loading ? 'Saving city' : 'Save city'}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2" role="status" aria-live="polite">
              <span className="material-symbols-outlined animate-spin text-sm" aria-hidden="true">progress_activity</span>
              Detecting climate...
            </span>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </BottomSheet>
  );
}
